import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { MediaItem } from "@gooner/shared";
import { makeId } from "@gooner/shared";
import { adminApi, ApiHttpError } from "../lib/api";
import { ClipGrid } from "./ClipGrid";
import { Button, Field, Panel, TextInput, cx } from "../control/ui/primitives";

/**
 * A remembered search (the full form state, so it round-trips exactly).
 * Persisted in localStorage — click a chip to re-run that search.
 *
 * The key predates the remote tabs' removal, so old entries may carry a
 * `tab` of "redgifs"/"reddit"; loadSaved drops those. Content enters the
 * catalog through the producer now — this browser only searches what we have.
 */
interface SavedSearch {
  id: string;
  label: string;
  tab: "catalog";
  catQ?: string;
}

const SAVED_KEY = "gooner.admin.savedSearches";

function loadSaved(): SavedSearch[] {
  try {
    const arr = JSON.parse(localStorage.getItem(SAVED_KEY) ?? "[]");
    return Array.isArray(arr)
      ? arr.filter(
          (x): x is SavedSearch =>
            x && typeof x.id === "string" && x.tab === "catalog",
        )
      : [];
  } catch {
    return [];
  }
}

function persistSaved(list: SavedSearch[]): void {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable — chips just won't persist */
  }
}

/**
 * The admin catalog browser, with a per-clip action slot. The caller decides what
 * "adding" a clip means via `renderAction` (a single Add button, or a
 * multi-collection picker). `onItemsChange` reports the current visible clips so a
 * caller can, e.g., look up their collection membership.
 *
 * This used to be a tabbed SOURCE browser with RedGifs and Reddit tabs — the
 * admin-side intake. Those are gone on purpose: the producer box is the only thing
 * that pulls content, so curation here works entirely over the existing catalog.
 */
export function CatalogBrowser({
  title = "Catalog",
  renderAction,
  renderTopRight,
  onClipClick,
  onItemsChange,
}: {
  title?: string;
  renderAction: (clip: MediaItem) => ReactNode;
  /** Always-visible top-right slot on each tile (e.g. current collection tags). */
  renderTopRight?: (clip: MediaItem) => ReactNode;
  /** Click a tile's media (e.g. to open a lightbox). */
  onClipClick?: (clip: MediaItem) => void;
  onItemsChange?: (items: MediaItem[]) => void;
}) {
  const [catQ, setCatQ] = useState("");
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped on a fresh fetch so the grid jumps back to page 1.
  const [resetToken, setResetToken] = useState(0);

  const [saved, setSaved] = useState<SavedSearch[]>(() => loadSaved());
  // Bumped by applySearch to fetch *after* the restored form state commits.
  const [runToken, setRunToken] = useState(0);

  // Report the visible clips to the caller without making the callback a render dep.
  const notify = useRef(onItemsChange);
  notify.current = onItemsChange;
  useEffect(() => {
    notify.current?.(items);
  }, [items]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResetToken((t) => t + 1);
    try {
      const { items: found } = await adminApi.listCatalogClips(catQ.trim() || undefined);
      setItems(found);
    } catch (e) {
      const msg =
        e instanceof ApiHttpError ? e.message : e instanceof Error ? e.message : String(e);
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [catQ]);

  // Re-run a search once applySearch's restored form state has committed.
  useEffect(() => {
    if (runToken > 0) void fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runToken]);

  function saveCurrent() {
    const entry: SavedSearch = {
      id: makeId(),
      label: `Catalog · ${catQ.trim() || "all"}`,
      tab: "catalog",
      catQ,
    };
    // Replace any existing chip with the same label so re-saving isn't a dupe.
    const next = [...saved.filter((s) => s.label !== entry.label), entry];
    setSaved(next);
    persistSaved(next);
  }

  function applySearch(s: SavedSearch) {
    setCatQ(s.catQ ?? "");
    setItems([]);
    setError(null);
    setRunToken((t) => t + 1);
  }

  function removeSearch(id: string) {
    const next = saved.filter((s) => s.id !== id);
    setSaved(next);
    persistSaved(next);
  }

  return (
    <Panel title={title}>
      <div className="space-y-4">
        <Field label="Search catalog" hint="everything the producer has shipped">
          <TextInput
            value={catQ}
            onChange={(e) => setCatQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void fetchPage()}
            placeholder="Filter by id / source…"
          />
        </Field>

        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" size="sm" onClick={() => void fetchPage()} disabled={loading}>
            {loading ? "Loading…" : "Search"}
          </Button>
          <Button variant="subtle" size="sm" onClick={saveCurrent}>
            Save search
          </Button>
          {error ? <span className="text-xs text-red-400">{error}</span> : null}
        </div>

        {saved.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wide text-zinc-500">Saved</span>
            {saved.map((s) => (
              <span
                key={s.id}
                className={cx(
                  "inline-flex items-center gap-1 rounded-full border border-[var(--color-edge)]",
                  "bg-[var(--color-panel-2)] py-0.5 pl-2.5 pr-1.5 text-xs text-zinc-200",
                )}
              >
                <button
                  type="button"
                  onClick={() => applySearch(s)}
                  className="hover:text-[var(--color-accent-soft)]"
                  title="Run this search"
                >
                  {s.label}
                </button>
                <button
                  type="button"
                  aria-label={`Delete saved search ${s.label}`}
                  onClick={() => removeSearch(s.id)}
                  className="leading-none text-zinc-500 hover:text-red-300"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <ClipGrid
            items={items}
            renderAction={renderAction}
            renderTopRight={renderTopRight}
            onClipClick={onClipClick}
            resetKey={String(resetToken)}
          />
        )}
      </div>
    </Panel>
  );
}
